import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { talentyApi, type DomainContentItem, type JobLandingPage } from '../services/talentyApi';
import './DomainsOverviewPage.css';

const DomainsOverviewPage: React.FC = () => {
  const [domains, setDomains] = useState<DomainContentItem[]>([]);
  const [allJobs, setAllJobs] = useState<JobLandingPage[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);
        // Fetch both domains and jobs to get complete hierarchical information
        const [domainsResponse, jobsResponse] = await Promise.all([
          talentyApi.getDomainContentItemsOverview(),
          talentyApi.getAllJobLandingPages()
        ]);
        
        // Debug: Log the API responses
        console.log('=== API DEBUG INFO ===');
        console.log('Domains Response:', domainsResponse);
        console.log('Jobs Response:', jobsResponse);
        console.log('Individual Domain Items:');
        domainsResponse.forEach((domain, index) => {
          console.log(`Domain ${index}:`, {
            id: domain.id,
            slug: domain.slug,
            title: domain.title,
            familySlugs: domain.familySlugs,
            jobSlugs: domain.jobSlugs,
            domain: domain.domain,
            family: domain.family
          });
        });
        console.log('Individual Job Items:');
        jobsResponse.pages.slice(0, 5).forEach((job, index) => {
          console.log(`Job ${index}:`, {
            slug: job.slug,
            title: job.title,
            domainSlug: job.domainSlug,
            familySlug: job.familySlug,
            domain: job.domain,
            family: job.family
          });
        });
        console.log('=== END DEBUG INFO ===');
        
        setDomains(domainsResponse);
        setAllJobs(jobsResponse.pages);
      } catch (err) {
        console.error('Error fetching data:', err);
        setError('Die Bereiche konnten nicht geladen werden.');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  // Helper function to get family slugs for a domain
  const getFamilySlugsForDomain = (domain: DomainContentItem) => {
    return domain.familySlugs || [];
  };

  // Helper function to get jobs for a domain
  const getJobsForDomain = (domain: DomainContentItem) => {
    return allJobs.filter(job => job.domainSlug === domain.slug);
  };

  // Helper function to get job slugs for a domain
  const getJobSlugsForDomain = (domain: DomainContentItem) => {
    const domainJobs = getJobsForDomain(domain);
    return domainJobs.map(job => job.slug);
  };

  // Helper function to get job count for a domain
  const getJobCountForDomain = (domain: DomainContentItem) => {
    const domainJobs = getJobsForDomain(domain);
    return domainJobs.length;
  };

  if (loading) {
    return (
      <div className="domain-list-page">
        <div className="container">
          <div className="loading">Lade Bereiche...</div>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="domain-list-page">
        <div className="container">
          <div className="error">
            <h2>Fehler beim Laden der Bereiche</h2>
            <p>{error}</p>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="domain-list-page">
      <div className="container">
        <div className="page-header">
          <h1>Bereiche bei Digital Workforce Group</h1>
          <p>Entdecke unsere verschiedenen Bereiche</p>
        </div>

        <div className="domains-grid">
          {domains.map(domain => {
            const familySlugs = getFamilySlugsForDomain(domain);
            const jobSlugs = getJobSlugsForDomain(domain);
            const jobCount = getJobCountForDomain(domain);
            
            return (
              <div key={domain.id} className="domain-card">
                <div className="domain-header">
                  <h3 className="domain-title">{domain.title}</h3>
                  <div className="domain-stats">
                    {familySlugs.length > 0 && (
                      <span className="stat-badge">
                        {familySlugs.length} Familie{familySlugs.length !== 1 ? 'n' : ''}
                      </span>
                    )}
                    {jobCount > 0 && (
                      <span className="stat-badge">
                        {jobCount} Job{jobCount !== 1 ? 's' : ''}
                      </span>
                    )}
                  </div>
                </div>
                
                {familySlugs.length > 0 && (
                  <div className="domain-families">
                    <h4>Jobfamilien:</h4>
                    <div className="families-list">
                      {familySlugs.map((familySlug: string, index: number) => (
                        <Link
                          key={index}
                          to={`/family/${familySlug}`}
                          className="family-tag family-link"
                        >
                          {familySlug}
                        </Link>
                      ))}
                    </div>
                  </div>
                )}
                
                {/* Hidden by default, shown on hover */}
                {jobSlugs.length > 0 && (
                  <div className="domain-jobs-hover">
                    <h4>Job Slugs:</h4>
                    <div className="jobs-slugs-list">
                      {jobSlugs.slice(0, 8).map((jobSlug: string, index: number) => (
                        <Link
                          key={index}
                          to={`/job/${jobSlug}`}
                          className="job-slug-link"
                        >
                          {jobSlug}
                        </Link>
                      ))}
                      {jobSlugs.length > 8 && (
                        <span className="more-jobs">
                          und {jobSlugs.length - 8} weitere Jobs...
                        </span>
                      )}
                    </div>
                  </div>
                )}
                
                <div className="domain-actions">
                  <Link to={`/domain/${domain.slug}`} className="btn btn-primary">
                    Bereich ansehen
                  </Link>
                  {jobCount > 0 && (
                    <Link to={`/jobs?domain=${domain.slug}`} className="btn btn-secondary">
                      Alle Jobs anzeigen
                    </Link>
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {domains.length === 0 && (
          <div className="no-domains">
            <h3>Keine Bereiche gefunden</h3>
            <p>Komm später wieder.</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default DomainsOverviewPage;
